import React, { useState, useEffect } from 'react';
import { FiActivity, FiUsers, FiTrendingUp } from 'react-icons/fi';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { ownerAnalyticsAPI } from '../../../api/ownerEndpoints';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import { showToast } from '../../../components/common/Toaster';
import { useTheme } from '../../../contexts/ThemeContext';
import { safeParseFloat } from './utils';
import {
  Section,
  SectionTitle,
  Grid,
  StatCard,
  StatLabel,
  StatValue,
  StatDescription,
  Card,
  ChartContainer,
  LoadingContainer,
  EmptyState,
} from './Analytics.styles';

const Engagement = ({ startDate, endDate }) => {
  const { theme } = useTheme();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchData();
  }, [startDate, endDate]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = { 
        start_date: startDate ? `${startDate}T00:00:00Z` : undefined,
        end_date: endDate ? `${endDate}T23:59:59Z` : undefined,
      };
      const response = await ownerAnalyticsAPI.getEngagement(params);
      setData(response.data);
    } catch (error) {
      console.error('Error fetching engagement:', error);
    } finally {
      setLoading(false);
    }
  };

  const prepareDAUData = () => {
    if (!data?.dau) return [];
    return data.dau.map((item) => ({
      day: new Date(item.day).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      users: safeParseFloat(item.active_users),
    }));
  };

  const prepareWAUData = () => {
    if (!data?.wau) return [];
    return data.wau.map((item) => ({
      week: `Week ${item.week.split('-')[1]}`,
      users: safeParseFloat(item.active_users),
    }));
  };

  const prepareMAUData = () => {
    if (!data?.mau) return [];
    return data.mau.map((item) => ({
      month: item.month.split('-')[1],
      users: safeParseFloat(item.active_users),
    }));
  };

  if (loading) {
    return (
      <LoadingContainer>
        <LoadingSpinner size="large" text="Loading engagement metrics..." />
      </LoadingContainer>
    );
  }

  if (!data) {
    return (
      <EmptyState>
        <FiActivity size={64} />
        <h3>No Engagement Data</h3>
        <p>Engagement analytics will appear when available</p>
      </EmptyState>
    );
  }

  const dauData = prepareDAUData();
  const wauData = prepareWAUData();
  const mauData = prepareMAUData();

  return (
    <>
      <Section>
        <SectionTitle>
          <FiActivity />
          Platform Engagement
        </SectionTitle>
        <Grid minWidth="300px">
          <Card>
            <h3 style={{ marginBottom: '16px', color: theme.colors.textPrimary }}>Daily Active Users</h3>
            <ChartContainer height="200px">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={dauData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.colors.border} />
                  <XAxis dataKey="day" stroke={theme.colors.textSecondary} />
                  <YAxis stroke={theme.colors.textSecondary} />
                  <Tooltip 
                    contentStyle={{
                      backgroundColor: theme.colors.surface,
                      border: `1px solid ${theme.colors.border}`,
                      borderRadius: theme.borderRadius.md,
                    }}
                  />
                  <Line type="monotone" dataKey="users" stroke={theme.colors.primary} strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </ChartContainer>
          </Card>

          <Card>
            <h3 style={{ marginBottom: '16px', color: theme.colors.textPrimary }}>Weekly Active Users</h3>
            <ChartContainer height="200px">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={wauData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.colors.border} />
                  <XAxis dataKey="week" stroke={theme.colors.textSecondary} />
                  <YAxis stroke={theme.colors.textSecondary} />
                  <Tooltip 
                    contentStyle={{
                      backgroundColor: theme.colors.surface,
                      border: `1px solid ${theme.colors.border}`,
                      borderRadius: theme.borderRadius.md,
                    }}
                  />
                  <Line type="monotone" dataKey="users" stroke={theme.colors.secondary} strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </ChartContainer>
          </Card>

          <Card>
            <h3 style={{ marginBottom: '16px', color: theme.colors.textPrimary }}>Monthly Active Users</h3>
            <ChartContainer height="200px">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={mauData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.colors.border} />
                  <XAxis dataKey="month" stroke={theme.colors.textSecondary} />
                  <YAxis stroke={theme.colors.textSecondary} />
                  <Tooltip 
                    contentStyle={{
                      backgroundColor: theme.colors.surface,
                      border: `1px solid ${theme.colors.border}`,
                      borderRadius: theme.borderRadius.md,
                    }}
                  />
                  <Line type="monotone" dataKey="users" stroke={theme.colors.accent} strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </ChartContainer>
          </Card>
        </Grid>
      </Section>
    </>
  );
};

export default Engagement;

